/**
 * [Insider+] Shared lifecycle hook for the Insider+ iframe-hosting pages.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import {
  issueSsoToken,
  getInsiderBenefits,
} from '../../request/insidersRequest';
import { selectAuthUser } from '../../store/selectors/userSelectors';
import { setInsiderBenefits } from '../../store/actions/user/userActions';
import {
  getTargetOrigin,
  isIframeOriginAllowed,
  isSafeNavigationUrl,
} from '../../security/config';
import {
  sendApiBaseUrlToIframe,
  sendSsoTokenToIframe,
  setupIframeTokenListener,
} from '../iframeJwtUtils';

const MAX_AUTH_RETRIES = 2;

/**
 * Shared lifecycle for the Insider+ iframe-hosting pages.
 *
 * Centralizes SSO token issue + ack retry, AUTH_FAILED recovery, subscription
 * refresh, API base URL push, and re-seeding the iframe src on location.key
 * changes (header re-clicks while already on the page).
 *
 * @param {Object} options
 * @param {() => string} options.buildIframeSrc - returns the iframe src for
 *   the current state. Called on mount and again on every location.key change.
 *   Memoize with useCallback so the effect dep array stays stable.
 * @param {(url: string) => void} [options.onNavigateUrl] - called when the
 *   iframe sends NAVIGATE_TO_URL with a URL that has passed the origin +
 *   protocol checks. Need not be memoized — held via ref internally.
 * @param {(event: MessageEvent) => void} [options.onIframeMessage] - optional
 *   additional postMessage handler (origin already validated). Need not be
 *   memoized.
 */
const useInsiderPlusIframe = ({
  buildIframeSrc,
  onNavigateUrl,
  onIframeMessage,
}) => {
  const dispatch = useDispatch();
  const user = useSelector(selectAuthUser);
  const location = useLocation();

  const iframeRef = useRef(null);
  const [ssoToken, setSsoToken] = useState(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [isRecovering, setIsRecovering] = useState(false);
  const [authFailed, setAuthFailed] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  // Synchronously initialize from buildIframeSrc so the first paint already
  // points at the right URL (matters for pages that derive src from storage).
  const [iframeSrc, setIframeSrc] = useState(buildIframeSrc);
  // iframeKey drives a React-level remount of the <iframe>. We bump it on
  // every location.key change so a header re-click while already on the
  // current page forces a full iframe reload even when buildIframeSrc
  // returns an unchanged URL — browsers don't reload an <iframe> when its
  // src is reassigned to the same value, so a state-only update wouldn't
  // be enough.
  const [iframeKey, setIframeKey] = useState(0);

  const retryCountRef = useRef(0);
  const isRecoveringRef = useRef(false);
  const isRetryingRef = useRef(false);
  const hasMountedRef = useRef(false);

  // Refs mirror dynamic values so the token-listener effect can register
  // exactly once. Re-registering on every state change races with in-flight
  // iframe requests: the previous listener's cleanup discards
  // pendingRequests in its closure, so any REQUEST_SSO_TOKEN that arrived
  // mid-recovery would be dropped.
  const ssoTokenRef = useRef(null);
  useEffect(() => {
    ssoTokenRef.current = ssoToken;
  }, [ssoToken]);

  const callbacksRef = useRef({ onNavigateUrl, onIframeMessage });
  useEffect(() => {
    callbacksRef.current = { onNavigateUrl, onIframeMessage };
  });

  const fetchSsoToken = useCallback(async () => {
    try {
      const response = await issueSsoToken();
      // Token can be at response.data.ssoToken OR directly at response.data
      const token = response?.data?.ssoToken || response?.data;
      if (token && typeof token === 'string') {
        setSsoToken(token);
        return true;
      }
      return false;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[useInsiderPlusIframe] Error fetching SSO token:', error);
      return false;
    }
  }, []);

  useEffect(() => {
    fetchSsoToken();
  }, [fetchSsoToken]);

  // NAVIGATE_TO_URL + page-specific messages from the iframe. Registered once;
  // callbacks read through the ref so consumers don't have to memoize.
  // Non-NAVIGATE_TO_URL messages (e.g. IFRAME_ROUTE_CHANGED, NPS_ELIGIBLE) are
  // forwarded to the page's onIframeMessage callback.
  useEffect(() => {
    const handleMessage = (event) => {
      if (!isIframeOriginAllowed(event.origin)) return;
      if (event.data?.type === 'NAVIGATE_TO_URL' && event.data?.url) {
        if (
          isSafeNavigationUrl(event.data.url) &&
          callbacksRef.current.onNavigateUrl
        ) {
          callbacksRef.current.onNavigateUrl(event.data.url);
        }
        return;
      }
      if (callbacksRef.current.onIframeMessage) {
        callbacksRef.current.onIframeMessage(event);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Handlers stored in refs so the token-listener effect can stay
  // []-deps. Retry budget is gated on retryCountRef directly (rather than
  // just isRecoveringRef) so a burst of AUTH_FAILED events after recovery
  // resolves can't bypass MAX_AUTH_RETRIES.
  const handleAuthFailureRef = useRef(null);
  handleAuthFailureRef.current = async () => {
    if (isRecoveringRef.current) return;
    if (retryCountRef.current >= MAX_AUTH_RETRIES) {
      setAuthFailed(true);
      return;
    }
    retryCountRef.current += 1;
    isRecoveringRef.current = true;
    setIsRecovering(true);
    setSsoToken(null);
    try {
      await fetchSsoToken();
    } finally {
      isRecoveringRef.current = false;
      setIsRecovering(false);
    }
  };

  const handleSubscriptionSuccessRef = useRef(null);
  handleSubscriptionSuccessRef.current = () => {
    if (user?.UserUid) {
      getInsiderBenefits(user.UserUid)
        .then(({ data }) => dispatch(setInsiderBenefits(data)))
        .catch(() => {});
    }
  };

  useEffect(() => {
    // Route REQUEST_SSO_TOKEN responses through sendSsoTokenToIframe so
    // child-initiated requests get the same nonce + generation guarantees
    // as parent-initiated pushes. Without this, the listener's inline
    // response could carry a stale token (ssoTokenRef may lag setSsoToken
    // by one effect tick) and clobber a freshly pushed value.
    //
    // Propagate sendSsoTokenToIframe's boolean — it returns false when the
    // iframe element is mounted but its contentWindow is null (e.g. during
    // the brief window between key-bump remount and the new iframe load).
    // Lying "sent" in that gap would let the listener drop the pending
    // request and miss the eventual delivery once the iframe is ready.
    const sendSsoToken = () => {
      if (!ssoTokenRef.current) return false;
      return sendSsoTokenToIframe(
        iframeRef.current,
        ssoTokenRef.current,
        getTargetOrigin(),
      );
    };

    const cleanup = setupIframeTokenListener(
      () => null, // No JWT token for these pages
      () => ssoTokenRef.current,
      () => handleAuthFailureRef.current?.(),
      () => handleSubscriptionSuccessRef.current?.(),
      () => process.env.REACT_APP_BASE_API_URL,
      sendSsoToken,
    );
    return cleanup;
  }, []);

  // Push SSO token once both the iframe is loaded and we have a token.
  useEffect(() => {
    if (iframeLoaded && ssoToken && iframeRef.current) {
      sendSsoTokenToIframe(iframeRef.current, ssoToken, getTargetOrigin());
    }
  }, [iframeLoaded, ssoToken]);

  // Push the GBI API base URL down to the iframe as soon as it loads so it
  // talks to the backend matching this parent's environment.
  useEffect(() => {
    if (iframeLoaded && iframeRef.current) {
      sendApiBaseUrlToIframe(
        iframeRef.current,
        process.env.REACT_APP_BASE_API_URL,
        getTargetOrigin(),
      );
    }
  }, [iframeLoaded]);

  const handleIframeLoad = useCallback(() => {
    setIframeLoaded(true);
  }, []);

  const handleRetry = useCallback(async () => {
    if (isRetryingRef.current) return;
    isRetryingRef.current = true;
    setIsRetrying(true);
    retryCountRef.current = 0;
    try {
      // Only close the modal if the fetch actually produced a new token.
      // A silent failure would otherwise hide the error UI without fixing
      // anything, leaving the user with a still-broken iframe.
      const success = await fetchSsoToken();
      if (success) {
        setAuthFailed(false);
      }
    } finally {
      isRetryingRef.current = false;
      setIsRetrying(false);
    }
  }, [fetchSsoToken]);

  // Re-navigating to the same parent route (e.g. clicking the header link
  // while already on a deep iframe page) keeps the parent URL unchanged, so
  // the iframe never reloads on its own. Bump iframeKey so React remounts
  // the <iframe>, re-derive src for pages that compute it dynamically, and
  // refetch the SSO token so the push-token effect re-arms for the
  // reloaded child. The explicit setIframeLoaded(false) is load-bearing:
  // remounting the DOM element doesn't reset React state on its own, and
  // without this the push-token effect would fire against the new (still
  // loading) iframe before its onLoad rearms iframeLoaded.
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    setIframeLoaded(false);
    setSsoToken(null);
    setIframeSrc(buildIframeSrc());
    setIframeKey((k) => k + 1);
    fetchSsoToken();
  }, [location.key, buildIframeSrc, fetchSsoToken]);

  return {
    iframeRef,
    iframeSrc,
    iframeKey,
    iframeLoaded,
    isRecovering,
    isRetrying,
    authFailed,
    handleIframeLoad,
    handleRetry,
  };
};

export default useInsiderPlusIframe;
